import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  MobileGalleryCarousel,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Tabbed photo gallery — a single grid fed by the whole `photo_gallery`
 * collection, with filter tabs derived from the distinct photo `title`
 * values (plus a leading "All" tab that shows everything). Selecting a tab
 * narrows the grid to photos whose `title` matches; clicking a photo opens
 * the shared lightbox.
 *
 * This replaces the older split apartment/separator/community sections with
 * the single tabbed layout used on the reference demo. Categories are
 * data-driven, so a property that tags photos "Amenities" / "Community" (or
 * any other titles) gets a tab per category automatically — no per-property
 * code changes.
 */
const ALL_TAB = '__all__';

// The property photo gallery sometimes includes the property logo as a photo.
// It reads as a stray graphic in a photo grid, so drop any image whose title,
// alt text, description, or URL is tagged as a logo before anything downstream
// (tabs, grid, carousel, lightbox) sees it.
const LOGO_PATTERN = /\blogos?\b/i;

function isLogo(img: GalleryImage): boolean {
  return [img.title, img.alt_text, img.description, img.image, img.original_url].some(
    (value) => !!value && LOGO_PATTERN.test(value),
  );
}

export function GalleryTabs() {
  const rawImages = useCollectionBinding<GalleryImage>('gallerytabs.images', {
    collection: 'photo_gallery',
  });

  const images = useMemo(() => rawImages.filter((img) => !isLogo(img)), [rawImages]);

  // Distinct, non-empty titles in first-seen order become the category tabs.
  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const img of images) {
      const title = (img.title || '').trim();
      if (title && !seen.includes(title)) seen.push(title);
    }
    return seen;
  }, [images]);

  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Filtered view for the active tab. Closing the lightbox on tab change keeps
  // indices consistent between the visible grid and the lightbox.
  const visibleImages = useMemo(() => {
    if (activeTab === ALL_TAB) return images;
    return images.filter((img) => (img.title || '').trim() === activeTab);
  }, [images, activeTab]);

  const selectTab = (tab: string) => {
    setActiveTab(tab);
    setSelectedIndex(null);
  };

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        {/* Only show tabs when there is more than one category to switch between. */}
        {categories.length > 1 && (
          <div className="gallery-tabs__nav" role="tablist" aria-label="Gallery categories">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === ALL_TAB}
              className={`gallery-tabs__tab${activeTab === ALL_TAB ? ' is-active' : ''}`}
              onClick={() => selectTab(ALL_TAB)}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={activeTab === cat}
                className={`gallery-tabs__tab${activeTab === cat ? ' is-active' : ''}`}
                onClick={() => selectTab(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Tablet / desktop: multi-column grid */}
        <div className="gallery-tabs__grid hidden sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {visibleImages.map((img, index) => (
            <div
              key={`${activeTab}-${img.image || img.original_url || index}`}
              className="aspect-[4/3] overflow-hidden group cursor-pointer gallery-item"
              onClick={() => setSelectedIndex(index)}
            >
              <VibesitesImage
                url={img.image || img.original_url || ''}
                alt={img.alt_text || img.title || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
              />
            </div>
          ))}
        </div>

        {/* Mobile: single-image swipeable carousel */}
        <div className="sm:hidden">
          <MobileGalleryCarousel
            key={activeTab}
            images={visibleImages}
            onSelect={setSelectedIndex}
            showDots={false}
          />
        </div>
      </div>

      <GalleryLightbox
        images={visibleImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        .gallery-tabs__nav {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 2rem;
        }

        .gallery-tabs__tab {
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text-muted);
          background: transparent;
          border: none;
          border-radius: 3px;
          padding: 0.6rem 1.4rem;
          cursor: pointer;
          transition: color 0.2s, background-color 0.2s;
        }
        .gallery-tabs__tab:hover {
          color: var(--vibe-color-primary);
        }
        .gallery-tabs__tab.is-active {
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
        }
        .gallery-tabs__tab.is-active:hover {
          color: var(--vibe-color-text-on-primary);
        }

        @media (max-width: 639px) {
          .gallery-tabs__nav {
            gap: 0.25rem;
            margin-bottom: 1.25rem;
          }
          .gallery-tabs__tab {
            font-size: 0.85rem;
            padding: 0.5rem 0.9rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryTabs',
  displayName: 'Tabbed Gallery',
  tokens: {},
  collectionBindings: {
    'gallerytabs.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description:
        'Pulled from the property photo gallery. Filter tabs are generated automatically from the distinct photo titles (e.g. "Amenities", "Community"), with a leading "All" tab that shows every photo.',
    },
  },
};

(GalleryTabs as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryTabs;
